import { bindIdentifier, bindPropertyGetter } from '..'
import objToArray from '../../objToArray'
import urlForItem from '../../urlForItem'
import urlForKit from '../../urlForKit'
import urlForUser from '../../urlForUser'
import maybe from '../../maybe'
import itemTypes from '../../../enums/itemTypes'
import itemFormatting from '../../itemFormatting'
import searchTypes from '../../../enums/searchTypes'
import entityTypes from '../../../enums/entityTypes'
import mergeThings from '../mergeThings'
import Item from '../Item'
import that from '../../translationHelper'
import Kit from '../Kit'
import Colour from '../Colour'

// define the things
const thingIdentifiers = {
	[entityTypes.ITEM.type]: thing => !!thing.itemKey,
	[entityTypes.KIT.type]: thing => !!thing.kitId,
	[entityTypes.USER.type]: thing => !!(thing.userName && !thing.postId),
	[entityTypes.POST.type]: thing => !!thing.postId
}

const thingIdentifier = bindIdentifier(thingIdentifiers)
const thingPropertyGetter = bindPropertyGetter(thingIdentifier)

// define the property getters
const thingDescriptionGetters = {
	[entityTypes.USER.type]: user => user.fullName,
	[entityTypes.POST.type]: post => post.caption,
	default: thing => thing.description
}
const thingHrefGetters = {
	[entityTypes.ITEM.type]: item => urlForItem(item),
	[entityTypes.KIT.type]: kit => urlForKit(kit),
	[entityTypes.USER.type]: user => urlForUser({ ...user, isActivated: true })
}
const thingIdObjectGetters = {
	[entityTypes.ITEM.type]: item => ({ itemKey: item.itemKey }),
	[entityTypes.KIT.type]: kit => ({ kitId: kit.kitId }),
	[entityTypes.USER.type]: user => ({ uid: user.uid })
}
const thingIdValueGetters = {
	[entityTypes.ITEM.type]: item => item.itemKey,
	[entityTypes.KIT.type]: kit => kit.kitId,
	[entityTypes.USER.type]: user => user.uid
}

const thingImagesGetters = {
	[entityTypes.ITEM.type]: Item.getImages,
	default: thing => thing.images
}

const thingIdValueGetter = thingPropertyGetter(thingIdValueGetters)

const thingKeyGetters = {
	[entityTypes.ITEM.type]: item => item && `${thingIdentifier(item)}_${thingIdValueGetter(item)}_${Colour.canonicalName(Item.getColourName(item) || Item.allColours)}`,
	default: thing => thing && `${thingIdentifier(thing)}_${thingIdValueGetter(thing)}`
}

const thingKudosCountGetters = {
	[entityTypes.POST.type]: thing => thing.kudosCount
}

const thingFollowGetters = {
	[entityTypes.USER.type]: thing => thing.following
}
const thingShowOwnWantGetters = {
	[entityTypes.ITEM.type]: () => true,
	[entityTypes.KIT.type]: () => true,
	[entityTypes.USER.type]: () => false,
	[entityTypes.POST.type]: () => false
}
const thingShowKudosGetters = {
	[entityTypes.ITEM.type]: () => false,
	[entityTypes.KIT.type]: () => false,
	[entityTypes.USER.type]: () => false,
	[entityTypes.POST.type]: () => true
}
const thingSubtitleGetters = {
	[entityTypes.ITEM.type]: (item) => {
		let category = item.category
		if (category) {
			category = Array.isArray(category) ? category : [category]
			category = category.map(cat => (itemTypes[cat] ? itemTypes[cat].name : cat)).join(', ')
		}
		return category
	},
	[entityTypes.KIT.type]: kit => kit.fullName,
	[entityTypes.USER.type]: user => user.userName
}
const thingTitleGetters = {
	[entityTypes.ITEM.type]: itemFormatting.title,
	[entityTypes.KIT.type]: kit => kit.name,
	[entityTypes.USER.type]: user => user.fullName
}
const thingTypeNameGetters = {
	[entityTypes.ITEM.type]: () => that.context.translate('Item'),
	[entityTypes.KIT.type]: () => that.context.translate('Kit'),
	[entityTypes.USER.type]: () => that.context.translate('User')
}
const thingTypeBrandNameGetters = {
	[entityTypes.ITEM.type]: item => item.brandName
}
const thingCollectionGetters = {
	[entityTypes.ITEM.type]: () => 'items',
	[entityTypes.KIT.type]: () => 'kits',
	[entityTypes.USER.type]: () => 'userProfiles'
}

const thingDescriptionGetter = thingPropertyGetter(thingDescriptionGetters)
const thingHrefGetter = thingPropertyGetter(thingHrefGetters)
const thingIdObjectGetter = thingPropertyGetter(thingIdObjectGetters)
const thingImagesGetter = thingPropertyGetter(thingImagesGetters)
const thingKeyGetter = thingPropertyGetter(thingKeyGetters)
const thingShowOwnWantGetter = thingPropertyGetter(thingShowOwnWantGetters)
const thingShowKudosGetter = thingPropertyGetter(thingShowKudosGetters)
const thingFollowGetter = thingPropertyGetter(thingFollowGetters)
const thingKudosCountGetter = thingPropertyGetter(thingKudosCountGetters)
const thingSubtitleGetter = thingPropertyGetter(thingSubtitleGetters)
const thingTitleGetter = thingPropertyGetter(thingTitleGetters)
const thingTypeNameGetter = thingPropertyGetter(thingTypeNameGetters)
const thingTypeBrandNameGetter = thingPropertyGetter(thingTypeBrandNameGetters)
const thingCollectionGetter = thingPropertyGetter(thingCollectionGetters)

const sortThings = things =>
	things.sort((a, b) =>
		(thingTitleGetter(a) || '').localeCompare(thingTitleGetter(b) || ''))

const deduplicateThings = (things) => {
	const thingsByKey = things.reduce((a, thing) => ({ ...a, [thingKeyGetter(thing)]: thing }), {})
	return Object.keys(thingsByKey).map(thingKey => thingsByKey[thingKey])
}

const getThingSearchResults = (searchType = searchTypes.EVERYTHING.type, state = {}) => {
	let result = []
	if (searchType === searchTypes.ITEM.type) {
		result = state.searchItemResults
	} else if (searchType === searchTypes.KIT.type) {
		result = state.searchKitResults
	} else if (searchType === searchTypes.USER.type) {
		result = state.searchUserResults
	} else {
		result = mergeThings(state.searchItemResults, state.searchKitResults, state.searchUserResults)
	}
	return sortThings(result)
}

// some (more) helpful helpers
const isItem = thing => thingIdentifier(thing) === entityTypes.ITEM.type
const isKit = thing => thingIdentifier(thing) === entityTypes.KIT.type
const isUser = thing => thingIdentifier(thing) === entityTypes.USER.type

const isOwned = ({ itemOwnByKey, kitOwnById }) => thingPropertyGetter({
	[entityTypes.ITEM.type]: Item.isOwned(itemOwnByKey),
	[entityTypes.KIT.type]: Kit.isOwned(kitOwnById)
})

const isWanted = ({ itemWantByKey, kitWantById }) => thingPropertyGetter({
	[entityTypes.ITEM.type]: Item.isWanted(itemWantByKey),
	[entityTypes.KIT.type]: Kit.isWanted(kitWantById)
})

const isSameThing = (thing, otherThing) => (isItem(thing) ? Item.isSameItem(thing, otherThing) : thingKeyGetter(thing) === thingKeyGetter(otherThing))

export const getThingTag = (thing, existingTag) => ({ // existingTag can be used to transform (for ex.) the item into a suitable shape (with colour, like algolia item) for making the (updated) tag
	...maybe({ text: thingTitleGetter(thing) }, thingTitleGetter(thing)),
	...maybe({ description: thingDescriptionGetter(thing) }, thingDescriptionGetter(thing)),
	...maybe({ images: thingImagesGetter(thing, existingTag) }, (thingImagesGetter(thing, existingTag) || []).length),
	...maybe({ type: thingIdentifier(thing) }, thingIdentifier(thing)),
	...thingIdObjectGetter(thing),
	...(!!isUser(thing) && {
		...maybe({ userName: thing.userName }, thing.userName),
		...maybe({ fullName: thing.fullName }, thing.fullName)
	}),
	...(!!isKit(thing) && {
		...maybe({ name: thing.name }, thing.name),
		...maybe({ fullName: thing.fullName }, thing.fullName),
		...maybe({ countItems: thing.items && thing.items.length }, thing.items && thing.items.length)
	}),
	...(!!isItem(thing) && {
		...maybe({ itemName: thing.itemName }, thing.itemName),
		...maybe({ category: thing.category }, thing.category),
		...maybe({ brandName: thing.brandName }, thing.brandName),
		...maybe({ variation: thing.variation }, thing.variation),
		...maybe({ colourName: (thing.colour && thing.colour.colourName) }, (thing.colour && thing.colour.colourName)),
		...maybe({ colourNames: thing.colourNames }, thing.colourNames),
		// only lead images for items
		...maybe({ images: objToArray(Colour.getLeadImages({ images: thingImagesGetter(thing, existingTag) })) }, (thingImagesGetter(thing, existingTag) || []).length)
	})
})

export default {
	// provide an identifier function
	identifier: thingIdentifier,
	// provide a get property getter function
	propertyGetter: thingPropertyGetter,
	// provide search results getter, merge, sort, etc.
	getSearchResults: getThingSearchResults,
	merge: mergeThings,
	sort: sortThings,
	deduplicate: deduplicateThings,
	// provide the property getter functions
	description: thingDescriptionGetter, // TODO: when renaming item stuff to entity, prefix these with entity to help indicate that they take an entity
	href: thingHrefGetter,
	idObject: thingIdObjectGetter,
	id: thingIdValueGetter,
	images: thingImagesGetter,
	key: thingKeyGetter,
	showOwnWant: thingShowOwnWantGetter,
	showKudos: thingShowKudosGetter,
	follow: thingFollowGetter,
	kudosCount: thingKudosCountGetter,
	subtitle: thingSubtitleGetter,
	title: thingTitleGetter,
	typeName: thingTypeNameGetter,
	brandName: thingTypeBrandNameGetter,
	collection: thingCollectionGetter,
	// provide some other helpers
	isItem,
	isKit,
	isUser,
	isOwned,
	isWanted,
	isSameThing,
	getTag: getThingTag
}



// WEBPACK FOOTER //
// ./src/utilities/Data/Thing/index.js